/**
 * 使用趋势折线图与模型使用分布环形图（旧版 TrendChart / PieChart），配色来自主题色板。
 * 旧版趋势图把成本乘 100（按"分"显示），图例却写「成本($)」；这里按美元原值显示。
 */
import { useMemo } from 'react'
import { Empty } from 'antd'
import dayjs from 'dayjs'
import { EChart } from '@/components/charts/EChart'
import { buildDonutOption, buildTrendLineOption } from '@/components/charts/presets'
import type { ModelRankingRow, TrendPoint } from './useTokenStats'
import styles from './TokenUsagePage.module.css'

const CHART_HEIGHT = 350

// 与旧版饼图一致：取模型排行前 6 个
const DISTRIBUTION_LIMIT = 6

function ChartEmpty() {
  return <Empty className={styles.chartEmpty} image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
}

export function TrendChart({ points }: Readonly<{ points: TrendPoint[] }>) {
  const option = useMemo(
    () =>
      buildTrendLineOption(
        points.map((point) => dayjs(point.date).format('MM-DD')),
        [
          { name: '请求次数', data: points.map((point) => point.requests) },
          { name: 'Token消耗', data: points.map((point) => point.tokens) },
          { name: '成本($)', data: points.map((point) => point.cost ?? 0) }
        ]
      ),
    [points]
  )
  if (points.length === 0) return <ChartEmpty />
  return <EChart option={option} height={CHART_HEIGHT} ariaLabel="使用趋势" />
}

// 按成本画前 6 个模型
export function ModelDistributionChart({ models }: Readonly<{ models: ModelRankingRow[] }>) {
  const top = useMemo(() => models.slice(0, DISTRIBUTION_LIMIT), [models])
  const option = useMemo(() => {
    const base = buildDonutOption(top.map((item) => ({ name: `${item.provider}/${item.model}`, value: item.cost || 0 })))
    const [pie] = base.series as Record<string, unknown>[]
    // 模型名较长，半宽卡片里图例放在右侧会压住圆环，改放到下方
    return {
      ...base,
      legend: { ...(base.legend as Record<string, unknown>), orient: 'horizontal', right: undefined, top: undefined, bottom: 0 },
      series: [{ ...pie, center: ['50%', '45%'] }]
    }
  }, [top])
  // 全部为 0 时环形图画不出扇区，按空数据处理
  if (!top.some((item) => (item.cost || 0) > 0)) return <ChartEmpty />
  return <EChart option={option} height={CHART_HEIGHT} ariaLabel="模型使用分布" />
}
